import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';

interface Country {
  id: number;
  name: string;
  name_ar: string;
  code: string;
  flag_emoji: string;
}

interface Office {
  id: number;
  country_id: number;
  country_name: string;
  country_name_ar: string;
  country_code: string;
  country_flag: string;
  city_en: string;
  city_ar: string | null;
  address_en: string;
  address_ar: string | null;
  phone: string;
  email: string;
  timezone: string | null;
  display_order: number;
  is_active: boolean;
}

interface FormData {
  country_id: number | null;
  city_en: string;
  city_ar: string;
  address_en: string;
  address_ar: string;
  phone: string;
  email: string;
  timezone: string;
  is_active: boolean;
}

const OfficeManager: React.FC = () => {
  const [offices, setOffices] = useState<Office[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState<FormData>({
    country_id: null,
    city_en: '',
    city_ar: '',
    address_en: '',
    address_ar: '',
    phone: '',
    email: '',
    timezone: '',
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');

      // Fetch offices
      const officesResponse = await fetch('https://patrixglobal.com/backend/api/admin/offices/', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const officesData = await officesResponse.json();

      // Fetch countries
      const countriesResponse = await fetch('https://patrixglobal.com/backend/api/admin/countries/', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const countriesData = await countriesResponse.json();

      if (officesData.success) {
        setOffices(officesData.data?.offices || officesData.offices || []);
      } else {      }

      if (countriesData.success) {
        setCountries(countriesData.countries || []);
      } else {      }
    } catch (error) {      showMessage('error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const resetForm = () => {
    setFormData({
      country_id: null,
      city_en: '',
      city_ar: '',
      address_en: '',
      address_ar: '',
      phone: '',
      email: '',
      timezone: '',
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (office: Office) => {
    setFormData({
      country_id: office.country_id,
      city_en: office.city_en,
      city_ar: office.city_ar || '',
      address_en: office.address_en,
      address_ar: office.address_ar || '',
      phone: office.phone,
      email: office.email,
      timezone: office.timezone || '',
      is_active: office.is_active,
    });
    setEditingId(office.id);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const url = editingId
        ? `https://patrixglobal.com/backend/api/admin/offices/?id=${editingId}`
        : 'https://patrixglobal.com/backend/api/admin/offices/';

      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', editingId ? 'Office updated successfully' : 'Office created successfully');
        resetForm();
        fetchData();
      } else {
        showMessage('error', data.message || 'Operation failed');
      }
    } catch (error) {      showMessage('error', 'Failed to save office');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, cityName: string) => {
    if (!window.confirm(`Are you sure you want to delete the office in ${cityName}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`https://patrixglobal.com/backend/api/admin/offices/?id=${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', 'Office deleted successfully');
        fetchData();
      } else {
        showMessage('error', data.message || 'Failed to delete office');
      }
    } catch (error) {      showMessage('error', 'Failed to delete office');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className={`rounded-md p-4 ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Office Locations</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
        >
          {showForm ? 'Cancel' : '+ Add New Office'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-blue-50 p-6 rounded-lg space-y-4">
          <h4 className="font-semibold text-gray-900 mb-4">
            {editingId ? 'Edit Office' : 'Add New Office'}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Country Selector */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.country_id || ''}
                onChange={(e) => setFormData({ ...formData, country_id: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a country...</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.flag_emoji} {country.name}
                  </option>
                ))}
              </select>
            </div>

            {/* City English */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City (English) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.city_en}
                onChange={(e) => setFormData({ ...formData, city_en: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Dubai"
              />
            </div>

            {/* City Arabic */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City (Arabic)
              </label>
              <input
                type="text"
                value={formData.city_ar}
                onChange={(e) => setFormData({ ...formData, city_ar: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-right"
                placeholder="مثال: دبي"
                dir="rtl"
              />
            </div>

            {/* Address English */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address (English) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.address_en}
                onChange={(e) => setFormData({ ...formData, address_en: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Business Bay, Dubai"
              />
            </div>

            {/* Address Arabic */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address (Arabic)
              </label>
              <input
                type="text"
                value={formData.address_ar}
                onChange={(e) => setFormData({ ...formData, address_ar: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-right"
                placeholder="مثال: الخليج التجاري، دبي"
                dir="rtl"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="+971 4 123 4567"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="office@patrixglobal.com"
              />
            </div>

            {/* Timezone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timezone
              </label>
              <input
                type="text"
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="UTC+4"
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Active (visible on website)</span>
              </label>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Saving...</span>
                </>
              ) : (
                editingId ? 'Update Office' : 'Create Office'
              )}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Offices List */}
      <div className="space-y-4">
        {offices.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No offices configured yet.</p>
        ) : (
          offices.map((office) => (
            <div key={office.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">{office.country_flag}</div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {office.city_en}, {office.country_name}
                    </h4>
                    {office.city_ar && (
                      <p className="text-sm text-gray-600 mb-2" dir="rtl">
                        {office.city_ar}، {office.country_name_ar}
                      </p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mt-2">
                      <div>
                        <span className="font-medium">Address (EN):</span> {office.address_en}
                      </div>
                      {office.address_ar && (
                        <div dir="rtl">
                          <span className="font-medium">العنوان:</span> {office.address_ar}
                        </div>
                      )}
                      <div>
                        <span className="font-medium">Phone:</span> {office.phone}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {office.email}
                      </div>
                      {office.timezone && (
                        <div>
                          <span className="font-medium">Timezone:</span> {office.timezone}
                        </div>
                      )}
                      <div>
                        <span className={`inline-block px-2 py-1 rounded text-xs ${
                          office.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {office.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(office)}
                    className="text-blue-600 hover:text-blue-800 px-3 py-1 rounded border border-blue-600 hover:bg-blue-50"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(office.id, office.city_en)}
                    className="text-red-600 hover:text-red-800 px-3 py-1 rounded border border-red-600 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OfficeManager;
